import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cover } from 'types/cover';
import { coverExists, isNoDuplicate, removeCoverByPosition } from 'utils';

export interface SelectCover {
  covers: Cover[];
  validatedCovers: Cover[];
  completedCovers: Cover[];
  /**
   * @todo rename to restricted/removed/blocked/etc covers to better represent the business logic
   * as not all covers that go into this array need to necessarily be declined with a business rule.
   * i.e Business Interruption can implicitly be removed as a result of Building and Contents being
   * soft declined.
   */
  declinedCovers: Cover[];
}

const initialState: SelectCover = {
  covers: [Cover.Liability],
  /** @note covers that have been filled out correctly but not yet submitted */
  validatedCovers: [],
  /** @note covers that have been submitted and returned with no decline/referral */
  completedCovers: [],
  /** @note covers that have been soft declined and cannot be added again in the journey */
  declinedCovers: [],
};

export const selectCoverSlice = createSlice({
  name: 'selectCover',
  initialState,
  reducers: {
    addCover: (state, action: PayloadAction<Cover>) => {
      if (isNoDuplicate(state.covers, action.payload)) {
        state.covers.push(action.payload);
      }
    },
    removeCover: (state, action: PayloadAction<Cover>) => {
      coverExists(state.covers, action.payload) &&
        removeCoverByPosition(state.covers, action.payload);
      coverExists(state.validatedCovers, action.payload) &&
        removeCoverByPosition(state.validatedCovers, action.payload);
      coverExists(state.completedCovers, action.payload) &&
        removeCoverByPosition(state.completedCovers, action.payload);
    },
    addValidatedCover: (state, action: PayloadAction<Cover>) => {
      if (isNoDuplicate(state.validatedCovers, action.payload)) {
        state.validatedCovers.push(action.payload);
      }
    },
    removeValidatedCover: (state, action: PayloadAction<Cover>) => {
      coverExists(state.validatedCovers, action.payload) &&
        removeCoverByPosition(state.validatedCovers, action.payload);
      coverExists(state.completedCovers, action.payload) &&
        removeCoverByPosition(state.completedCovers, action.payload);
    },
    addCompletedCover: (state, action: PayloadAction<Cover>) => {
      if (isNoDuplicate(state.completedCovers, action.payload)) {
        state.completedCovers.push(action.payload);
      }
    },
    removeCompletedCover: (state, action: PayloadAction<Cover>) => {
      coverExists(state.completedCovers, action.payload) &&
        removeCoverByPosition(state.completedCovers, action.payload);
    },
    addDeclinedCover: (state, action: PayloadAction<Cover>) => {
      if (isNoDuplicate(state.declinedCovers, action.payload)) {
        state.declinedCovers.push(action.payload);
      }
    },
    removeDeclinedCover: (state, action: PayloadAction<Cover>) => {
      coverExists(state.declinedCovers, action.payload) &&
        removeCoverByPosition(state.declinedCovers, action.payload);
    },
    clearCovers: () => initialState,
  },
});

export const {
  addCover,
  removeCover,
  addValidatedCover,
  removeValidatedCover,
  addCompletedCover,
  removeCompletedCover,
  addDeclinedCover,
  removeDeclinedCover,
  clearCovers,
} = selectCoverSlice.actions;

export default selectCoverSlice.reducer;
